import type ServiceApi from "../../api";
import {LruCache} from "../../util/lruCache";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";

/** Set once when the cache first starts evicting, so the eviction log is only printed once. */
let tileCacheFullLogged = false;

/**
 * LRU cache for WMS/WMTS tile blob-URL promises, keyed by full tile URL. Capped at 64 MB.
 * Built primarily for #16885 (slow/uncached GeoServer WMS tiles on the map):
 * OL's built-in tile cache is lost whenever layers are recreated; this external
 * cache ensures already-fetched tiles are reused within a session.
 *
 * Module-level (not per ApiInterface instance): a fresh {@link GeoserverTileApiInterface} is
 * created per tile load, so the cache must outlive individual instances to be effective.
 */
const tileCache = new LruCache<string, Promise<string>>(64 * 1024 * 1024, (_key, promise, _bytes) => {
    if (!tileCacheFullLogged) {
        tileCacheFullLogged = true;
        console.log("#16885 tileCache is full, starting to evict old tiles.");
    }
    promise
        .then((blobUrl) => URL.revokeObjectURL(blobUrl))
        .catch(() => {
            /* already errored, nothing to revoke */
        });
});

/**
 * API interface for loading a single authenticated GeoServer tile (GET {@link RevaApi.PATH_GS} + tile URL).
 *
 * OpenLayers' default tile loader can't send the auth header, so its tileLoadFunction delegates here.
 * The fetched binary is turned into a blob URL (set as the tile image src) and cached in the
 * module-level {@link tileCache}; concurrent requests for the same tile share one in-flight promise.
 */
export class GeoserverTileApiInterface extends SimpleApiInterface<string> {
    constructor(private readonly relativeUrl: string) {
        super();
    }

    protected async executeInternal(executor: ApiExecutor, url: string, credentials: ApiCredentials): Promise<string> {
        const cached = tileCache.get(url);
        if (cached) return cached;
        const promise = executor.fetchGETBinary(url, credentials).then((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            tileCache.updateBytes(url, blob.size);
            return blobUrl;
        });
        tileCache.set(url, promise);
        return promise;
    }

    protected override buildUrl(api: ServiceApi): string {
        // relativeUrl is OpenLayers' ready-made "/wms?…" / "/wmts?…" tile URL (a full query URL,
        // not a path segment), so concatenate it directly instead of using buildRevaApiUrl()
        // (which would join/encode segments and mangle the query string).
        return api.revaApiUrl + RevaApi.PATH_GS + this.relativeUrl;
    }

    /**
     * Tiles are loaded one HTTP request per tile, which would flood the develop-mode console.
     * Successful tile loads are therefore not logged (failures still are, via the base class).
     */
    protected override logSuccessfulResponse(): void {
        // intentionally silent — see method doc
    }
}
